import { type Key } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Autocomplete } from '@ds';
import { AddressSearch, AdminFormRow, ConditionalRender } from '@ui';
import { useAddressForm } from '@/hooks/useAddressForm';
import { EmployeeFormValues } from '@/types';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { renderAutocompleteItem } from '@/utils/helpers';
import { useCountrySubdivisionInfo } from '@/hooks/useCountrySubdivisionInfo';
import { ControlledTextInput } from '../../inputs/ControlledTextInput';

type HomeLocationFieldsProps = {
  isReadOnly?: boolean;
  isTimezoneToClear?: boolean;
  isBaseLocationAndPreferencesShown?: boolean;
};

export const HomeLocationFields = ({
  isReadOnly,
  isTimezoneToClear,
  isBaseLocationAndPreferencesShown,
}: HomeLocationFieldsProps) => {
  const { control, setValue } = useFormContext<EmployeeFormValues>();
  const { countriesSelectList } = useCountriesData();
  const country = useWatch({ control, name: 'country' });
  const state = useWatch({ control, name: 'state' });

  const { countryHasStates, stateLabel: stateSubdivisionLabel } = useCountrySubdivisionInfo(country);
  const stateLabel = `Home Location ${stateSubdivisionLabel}`;

  const {
    addressValue,
    addressList,
    stateSelectList,
    onAddressSelect,
    onStateInputChange,
    onAddressInputChange,
    onCountryInputChange,
  } = useAddressForm();

  const onAddressSelection = (addressText: string) => {
    onAddressSelect(addressText, isTimezoneToClear);
    const selectedAddress = addressList.find((a) => a.id === addressText);
    const ianaTimezone = selectedAddress?.timeZone?.name || '';

    if (ianaTimezone) {
      setValue('homeLocationTimezone', ianaTimezone);
    }
  };

  const stateNotSelected = countryHasStates && !state;
  const addressDisabled = !country || stateNotSelected;

  return (
    <>
      <AdminFormRow title="Home Location Country" isRequired>
        <Controller
          name="country"
          control={control}
          render={({ field: { ref, value, onChange, ...field }, fieldState: { invalid, error } }) => (
            <Autocomplete
              placeholder="Choose an option"
              aria-label="Home Location Country"
              isDisabled={isReadOnly}
              defaultItems={countriesSelectList}
              isInvalid={invalid}
              errorMessage={error?.message}
              selectedKey={value || null}
              onSelectionChange={(key: Key | null) => {
                onChange(key);
                onCountryInputChange(key as string, isTimezoneToClear || !isBaseLocationAndPreferencesShown);
              }}
              {...field}
            >
              {renderAutocompleteItem}
            </Autocomplete>
          )}
        />
      </AdminFormRow>
      <ConditionalRender show={countryHasStates}>
        <AdminFormRow title={stateLabel} isRequired>
          <Controller
            name="state"
            control={control}
            render={({ field: { ref, value, onChange, ...field }, fieldState: { invalid, error } }) => (
              <Autocomplete
                placeholder="Choose an option"
                aria-label="Home Location State"
                isDisabled={isReadOnly}
                defaultItems={stateSelectList}
                isInvalid={invalid}
                errorMessage={error?.message}
                selectedKey={value || null}
                onSelectionChange={(key: Key | null) => {
                  onChange(key);
                  onStateInputChange(key as string, isTimezoneToClear || !isBaseLocationAndPreferencesShown);
                }}
                {...field}
              >
                {renderAutocompleteItem}
              </Autocomplete>
            )}
          />
        </AdminFormRow>
      </ConditionalRender>
      <AdminFormRow title="Home Location Address" isRequired>
        <Controller
          name="addressLine"
          control={control}
          render={({ field: { ref, onChange, ...field }, fieldState: { invalid, error } }) => (
            <AddressSearch
              addressList={addressList}
              onAddressSelect={onAddressSelection}
              onAddressInputChange={onAddressInputChange}
              isInvalid={invalid}
              isDisabled={isReadOnly || addressDisabled}
              errorMessage={error?.message}
              wrapperProps={{ 'aria-label': 'Search Address' }}
              {...field}
              value={addressValue ?? field.value}
              onChange={onChange}
            />
          )}
        />
      </AdminFormRow>
      <AdminFormRow title="Home Location City" isRequired>
        <ControlledTextInput name="city" aria-label="City" isDisabled />
      </AdminFormRow>
      <AdminFormRow title="Home Location Postal Code" isRequired>
        <ControlledTextInput name="postalCode" aria-label="Postal Code" isDisabled />
      </AdminFormRow>
    </>
  );
};
